import React from 'react';
import { AlertTriangle, Tag, CheckCircle } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';

interface TagValidationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  newTags: string[];
  existingTags: string[];
  onConfirm: () => void;
  onCancel: () => void;
  context: 'submission' | 'review';
  isLoading?: boolean;
}

const TagValidationDialog: React.FC<TagValidationDialogProps> = ({
  open,
  onOpenChange,
  newTags,
  existingTags,
  onConfirm,
  onCancel,
  context,
  isLoading = false
}) => {
  const handleClose = () => {
    if (!isLoading) {
      onCancel();
      onOpenChange(false);
    }
  };

  const handleConfirm = () => {
    onConfirm();
  };

  const contextText = context === 'submission' ? 'submitting this solution' : 'approving this suggestion';
  const actionText = context === 'submission' ? 'Submit Solution' : 'Approve Suggestion';

  return (
    <AlertDialog open={open} onOpenChange={handleClose}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            <AlertDialogTitle className="text-amber-900">
              New Tags Detected
            </AlertDialogTitle>
          </div>
          <AlertDialogDescription asChild>
            <div className="space-y-4">
              <p>
                You are about to create <strong>{newTags.length}</strong> tag{newTags.length !== 1 ? 's' : ''} by {contextText}. 
                This will add {newTags.length > 1 ? 'them' : 'it'} to the database permanently.
              </p>

              {newTags.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center gap-1 text-sm font-medium">
                    <Tag className="h-4 w-4" />
                    New tags to be created:
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {newTags.map((tag, index) => (
                      <Badge key={index} variant="outline" className="bg-amber-50 border-amber-200 text-amber-800">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {existingTags.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center gap-1 text-sm font-medium text-green-700">
                    <CheckCircle className="h-4 w-4" />
                    Existing tags (already in database):
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {existingTags.map((tag, index) => (
                      <Badge key={index} variant="outline" className="bg-green-50 border-green-200 text-green-800">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              <p className="text-sm text-muted-foreground">
                Are you sure you want to proceed and create {newTags.length > 1 ? 'these new tags' : 'this new tag'}?
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading} onClick={handleClose}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={isLoading}
            className="bg-amber-600 hover:bg-amber-700 focus:ring-amber-600"
          >
            {isLoading ? "Processing..." : `Proceed with ${actionText}`}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default TagValidationDialog; 